// Drafting a new version: the whole version on one screen, edited, then saved.
//
// Opened from a version ("Draft a new version from vN") with its parameters
// already filled in, because re-typing a signed version to change one limit is how
// the other fifteen get changed by accident. Nothing reaches the server until
// "Save as draft", and the saved draft still judges nothing: it goes into force
// only when somebody holding SPEC_APPROVE approves it on the page behind this one.
// The rules live in specDraft.ts, where they are tested.
//
// Each problem is said under its own field (UI/UX audit item 16, 27 Oct 2026):
// the input is `aria-invalid` and `aria-describedby` its sentence, shown once the
// field is left or a save is tried - never while a fresh field is being typed in.
// Save stays enabled; a save with problems moves focus to the first invalid field
// and lists every problem at the top as links to the fields.
//
// Opened with no version behind it (fromVersion null) from the spec master's "New
// draft" (UI/UX audit, 31 Oct 2026, UX-31): one blank row, the same fields in the
// same order, so a draft made there and a draft made here are the same form. The
// one-parameter form it replaces had its own field order, called FIRST_OFF "at
// start-up" (the operator's word for READINESS) and could not make a yes/no
// start-up check at all.
//
// A raw material's rows show what a certificate is held to (UX-29): a COFA or
// RECEIPT row judged by words shows its expected text in place of the measurement
// fields, every COFA row its mandatory flag, and the version's own terms are
// shown, read-only, at the top - each was carried silently before, so the person
// saving could not see what they were about to send for signature.
import { useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { AGGREGATION_LABEL, CHECK_KIND_LABEL } from '../../lib/vocabulary';
import { Button } from '@/components/ui/button';
import { Saved } from '@/components/Saved';
import { fieldClass, labelClass, officeFocus, serverMessage } from '../../shells/OfficeShell';
import {
  blankRow,
  checkDraft,
  isCertificateKind,
  isInProcess,
  judgedByText,
  rowFromApi,
  toPayload,
  versionTerms,
  type Aggregation,
  type ApiParameter,
  type CheckKind,
  type ControlPointType,
  type DraftField,
  type DraftProblem,
  type DraftRow,
  type ValueKind,
} from './specDraft';

interface SpecDraftEditorProps {
  subjectType: string;
  subjectId: number;
  /** The version the draft is made from; null = a new draft from nothing (UX-31), opened with one blank row. */
  fromVersion: number | null;
  initial: ApiParameter[];
  /** The version's own terms, carried (a MATERIAL's minimum shelf life and CofA flag). */
  terms?: { minShelfLifeDays?: number | null; cofaRequired?: boolean | null };
  isProduct: boolean;
  /** The new draft's id and the one-line status to show where it was opened from. */
  onSaved: (id: number, note: string) => void;
  onCancel: () => void;
}

// The words live in lib/vocabulary.ts, shared with the spec version page (UI/UX audit, 31 Oct
// 2026, UX-18). This list stays: it is the picker's order and a rule 14 mirror.
const CHECK_KINDS: Array<[CheckKind, string]> = [
  ['FIRST_OFF', CHECK_KIND_LABEL.FIRST_OFF],
  ['ADHERENCE', CHECK_KIND_LABEL.ADHERENCE],
  ['BOTH', CHECK_KIND_LABEL.BOTH],
  ['READINESS', CHECK_KIND_LABEL.READINESS],
  // A raw material's checks (migration 014): copied through when drafting from a
  // MATERIAL version. Its expected text and mandatory flag are carried, not edited here.
  ['COFA', CHECK_KIND_LABEL.COFA],
  ['RECEIPT', CHECK_KIND_LABEL.RECEIPT],
];

const FIELD = `${fieldClass} ${officeFocus}`;
/** The invalid field: the refusal colour on the border, never the OUT triad (index.css rule 1). */
const INVALID = 'border-(--warn-line)';

/** The input's id, and from it the id of the sentence under it. */
export const fieldId = (rowKey: string, field: DraftField) => `${rowKey}-${field}`;
const errorId = (rowKey: string, field: DraftField) => `${fieldId(rowKey, field)}-error`;

/** The four limits and the target: a problem between two of them belongs to all of them. */
const BAND_FIELDS: ReadonlySet<DraftField> = new Set<DraftField>(['minValue', 'actionLower', 'nominal', 'actionUpper', 'maxValue']);

export function SpecDraftEditor({
  subjectType,
  subjectId,
  fromVersion,
  initial,
  terms = {},
  isProduct,
  onSaved,
  onCancel,
}: SpecDraftEditorProps) {
  const [rows, setRows] = useState<DraftRow[]>(() =>
    initial.length > 0 || fromVersion !== null ? initial.map(rowFromApi) : [blankRow(isProduct)],
  );
  const title = fromVersion === null ? `New draft for ${subjectType} #${subjectId}` : `New draft from version ${fromVersion}`;
  const [why, setWhy] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ id: number; version: number | null } | null>(null);
  /** Fields the person has left (blurred): their problems are shown. */
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  /** A save was tried with problems standing: every problem is shown, and the summary. */
  const [submitted, setSubmitted] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const problems = useMemo(() => checkDraft(rows, isProduct), [rows, isProduct]);

  const update = (key: string, patch: Partial<DraftRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const move = (index: number, delta: -1 | 1) =>
    setRows((rs) => {
      const to = index + delta;
      if (to < 0 || to >= rs.length) return rs;
      const next = [...rs];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  const touch = (key: string, field: DraftField) =>
    setTouched((t) => (t.has(fieldId(key, field)) ? t : new Set(t).add(fieldId(key, field))));

  /** The problem to say under this field, if it is to be shown yet. */
  const problemOf = (rowIndex: number, field: DraftField): DraftProblem | null => {
    const key = rows[rowIndex].key;
    // A crossed band is put under the upper field of its pair, but is usually caused by editing the
    // other one: any band field touched shows the row's band problems (found live 27 Oct 2026 -
    // action upper set above reject max said nothing on blur).
    const seen = BAND_FIELDS.has(field)
      ? [...BAND_FIELDS].some((f) => touched.has(fieldId(key, f)))
      : touched.has(fieldId(key, field));
    if (!submitted && !seen) return null;
    return problems.errors.find((e) => e.row === rowIndex && e.field === field) ?? null;
  };

  /** The id a problem's link points at: its field, else its row's fieldset, else nothing. */
  const targetOf = (p: DraftProblem): string | null => {
    if (p.row === null) return null;
    const key = rows[p.row]?.key;
    if (!key) return null;
    return p.field ? fieldId(key, p.field) : `${key}-row`;
  };

  const focusTarget = (id: string | null) => {
    const el = id ? document.getElementById(id) : null;
    if (el) {
      el.focus();
      el.scrollIntoView?.({ block: 'center' });
    } else summaryRef.current?.focus();
  };

  async function save() {
    if (saving) return;
    if (problems.errors.length > 0) {
      setSubmitted(true);
      // The inputs are already on screen: nothing about them changes on this render.
      focusTarget(targetOf(problems.errors[0]));
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      const created = (await api.post('/specs', {
        subjectType,
        subjectId,
        ...(fromVersion !== null
          ? { notes: `Drafted from version ${fromVersion}.${why.trim() ? ` ${why.trim()}` : ''}` }
          : why.trim()
            ? { notes: why.trim() }
            : {}),
        ...versionTerms(subjectType, terms),
        parameters: toPayload(rows),
      })) as { id: number; version?: number };
      const version = typeof created.version === 'number' ? created.version : null;
      setSaved({ id: created.id, version });
      setSaving(false);
      onSaved(created.id, `${version === null ? 'Draft' : `Draft v${version}`} saved. It judges nothing until somebody holding SPEC_APPROVE approves it.`);
    } catch (err: unknown) {
      // The server's own sentence: a CHECK constraint names the exact field.
      setRefusal(serverMessage(err));
      setSaving(false);
    }
  }

  /** The sentence under a field, or nothing. Rendered right under the input it describes. */
  const errorLine = (row: DraftRow, i: number, field: DraftField) => {
    const p = problemOf(i, field);
    return p ? (
      <p id={errorId(row.key, field)} className="m-0 max-w-[32ch] text-[13px] font-semibold text-(--warn)">
        {p.message.replace(/^Row \d+(?: \([^)]*\))?: /, '')}
      </p>
    ) : null;
  };
  /** The attributes that mark an input invalid and name its sentence, and the class that colours it. */
  const invalid = (row: DraftRow, i: number, field: DraftField) =>
    problemOf(i, field) ? { 'aria-invalid': true as const, 'aria-describedby': errorId(row.key, field) } : {};
  const invalidClass = (_row: DraftRow, i: number, field: DraftField) => (problemOf(i, field) ? INVALID : '');

  const num = (row: DraftRow, i: number, field: DraftField, labelText: string) => {
    return (
      <div className="flex flex-col gap-(--s1)">
        <label htmlFor={fieldId(row.key, field)} className={labelClass}>
          {labelText}
        </label>
        <input
          id={fieldId(row.key, field)}
          type="text"
          inputMode="decimal"
          value={row[field] as string}
          onChange={(e) => update(row.key, { [field]: e.target.value } as Partial<DraftRow>)}
          onBlur={() => touch(row.key, field)}
          {...invalid(row, i, field)}
          className={`${FIELD} w-[110px] font-mono ${invalidClass(row, i, field)}`}
        />
        {errorLine(row, i, field)}
      </div>
    );
  };

  if (saved) {
    const name = saved.version === null ? 'Draft' : `Draft v${saved.version}`;
    return (
      <section
        aria-label={title}
        className="flex flex-col gap-(--s3) rounded-(--radius) border border-(--border) bg-(--card) p-(--s4) shadow-card"
      >
        <Saved>{name} saved. It judges nothing until somebody holding SPEC_APPROVE approves it.</Saved>
      </section>
    );
  }

  return (
    <section
      aria-label={title}
      className="flex flex-col gap-(--s4) rounded-(--radius) border border-(--border) bg-(--card) p-(--s4) shadow-card"
    >
      <div>
        <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">
          {title}
        </h2>
        <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
          Change what needs changing, then save. Saving creates a draft: it judges nothing until
          somebody holding SPEC_APPROVE approves it, and the version in force stays in force until
          then. Leave a limit empty for a one-sided band; the action band sits inside the reject band.
        </p>
        {/* UX-29 (UI/UX audit, 31 Oct 2026): a MATERIAL version's own terms are signed with it and
            sent with this draft unchanged (versionTerms). Shown so the person saving sees them; not
            edited here, as before. */}
        {subjectType === 'MATERIAL' ? (
          <p className="m-0 mt-(--s2) text-(length:--fs-sm)">
            <span className={labelClass}>Carried{fromVersion !== null ? ` from version ${fromVersion}` : ''}, signed with the draft</span>{' '}
            Shelf life on arrival:{' '}
            {terms.minShelfLifeDays == null ? 'no minimum' : `at least ${terms.minShelfLifeDays} days`}. Certificate:{' '}
            {terms.cofaRequired === false ? 'not required' : 'required'}.
          </p>
        ) : null}
      </div>

      {submitted && problems.errors.length > 0 ? (
        <div
          ref={summaryRef}
          role="alert"
          tabIndex={-1}
          className="rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) px-(--s3) py-(--s3) text-(length:--fs-sm) text-(--warn)"
        >
          <p className="m-0 font-semibold">
            Not saved: {problems.errors.length === 1 ? 'one thing' : `${problems.errors.length} things`} to fix first.
          </p>
          <ul className="m-0 mt-(--s1) list-disc pl-(--s4)">
            {problems.errors.map((e) => {
              const target = targetOf(e);
              return (
                <li key={`${e.row ?? 'version'}-${e.field ?? 'all'}-${e.message}`}>
                  {target ? (
                    <a
                      href={`#${target}`}
                      className={`underline ${officeFocus}`}
                      onClick={(ev) => {
                        ev.preventDefault();
                        focusTarget(target);
                      }}
                    >
                      {e.message}
                    </a>
                  ) : (
                    e.message
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {rows.map((row, i) => (
        <fieldset
          key={row.key}
          id={`${row.key}-row`}
          tabIndex={-1}
          className="m-0 flex flex-col gap-(--s3) rounded-(--radius-sm) border border-(--border) p-(--s3)"
        >
          <legend className={`px-(--s2) ${labelClass}`}>
            {i + 1}. {row.name.trim() || 'New parameter'}
          </legend>
          {/* Migration 034: the server copies the source parameter's live reference photos onto
              this row on Save. Said only when there are some; a row added here carries none. */}
          {row.sourceParameterId !== null && (row.referencePhotoCount ?? 0) > 0 ? (
            <p className="m-0 text-(length:--fs-xs) text-(--muted)">
              {row.referencePhotoCount} reference photo{row.referencePhotoCount === 1 ? '' : 's'} carried over
            </p>
          ) : null}

          <div className="flex flex-wrap items-end gap-(--s3)">
            <div className="flex flex-col gap-(--s1)">
              <label htmlFor={fieldId(row.key, 'code')} className={labelClass}>Code</label>
              <input
                id={fieldId(row.key, 'code')}
                value={row.code}
                onChange={(e) => update(row.key, { code: e.target.value })}
                onBlur={() => touch(row.key, 'code')}
                {...invalid(row, i, 'code')}
                className={`${FIELD} w-[160px] font-mono ${invalidClass(row, i, 'code')}`}
              />
              {errorLine(row, i, 'code')}
            </div>
            <div className="flex flex-col gap-(--s1)">
              <label htmlFor={fieldId(row.key, 'name')} className={labelClass}>Name</label>
              <input
                id={fieldId(row.key, 'name')}
                value={row.name}
                onChange={(e) => update(row.key, { name: e.target.value })}
                onBlur={() => touch(row.key, 'name')}
                {...invalid(row, i, 'name')}
                className={`${FIELD} min-w-[220px] ${invalidClass(row, i, 'name')}`}
              />
              {errorLine(row, i, 'name')}
            </div>
            <div className="flex flex-col gap-(--s1)">
              <label htmlFor={fieldId(row.key, 'checkKind')} className={labelClass}>Checked</label>
              <select
                id={fieldId(row.key, 'checkKind')}
                value={row.checkKind}
                onChange={(e) => update(row.key, { checkKind: e.target.value as CheckKind })}
                onBlur={() => touch(row.key, 'checkKind')}
                {...invalid(row, i, 'checkKind')}
                className={`${FIELD} ${invalidClass(row, i, 'checkKind')}`}
              >
                {CHECK_KINDS.map(([k, text]) => (
                  <option key={k} value={k}>{text}</option>
                ))}
              </select>
              {errorLine(row, i, 'checkKind')}
            </div>
            {isInProcess(row.checkKind) ? (
              <div className="flex flex-col gap-(--s1)">
                <label htmlFor={fieldId(row.key, 'intervalMinutes')} className={labelClass}>Every (minutes)</label>
                <input
                  id={fieldId(row.key, 'intervalMinutes')}
                  type="text"
                  inputMode="numeric"
                  value={row.intervalMinutes}
                  onChange={(e) => update(row.key, { intervalMinutes: e.target.value })}
                  onBlur={() => touch(row.key, 'intervalMinutes')}
                  {...invalid(row, i, 'intervalMinutes')}
                  className={`${FIELD} w-[110px] font-mono ${invalidClass(row, i, 'intervalMinutes')}`}
                />
                {errorLine(row, i, 'intervalMinutes')}
              </div>
            ) : null}
            {/* UX-29: not on a certificate row read as a number - "A measurement" said Identification
                ("Passes Test") was measured. Still drawn if one is yes/no, so nothing is hidden. */}
            {!isCertificateKind(row.checkKind) || row.valueKind === 'ATTRIBUTE' ? (
            <div className="flex flex-col gap-(--s1)">
              <label htmlFor={fieldId(row.key, 'valueKind')} className={labelClass}>Answer</label>
              <select
                id={fieldId(row.key, 'valueKind')}
                value={row.valueKind}
                onChange={(e) => update(row.key, { valueKind: e.target.value as ValueKind })}
                onBlur={() => touch(row.key, 'valueKind')}
                {...invalid(row, i, 'valueKind')}
                className={`${FIELD} ${invalidClass(row, i, 'valueKind')}`}
              >
                <option value="NUMERIC">A measurement</option>
                <option value="ATTRIBUTE">Yes / no</option>
              </select>
              {errorLine(row, i, 'valueKind')}
            </div>
            ) : null}
          </div>

          {isCertificateKind(row.checkKind) ? (
            <div className="flex flex-wrap items-end gap-(--s3)">
              <div className="flex min-w-0 flex-col gap-(--s1)">
                <label htmlFor={fieldId(row.key, 'expectedText')} className={labelClass}>
                  Certificate must read
                </label>
                <input
                  id={fieldId(row.key, 'expectedText')}
                  value={row.expectedText}
                  maxLength={200}
                  placeholder='e.g. Not detected in 25 g (empty when judged by a limit)'
                  onChange={(e) => update(row.key, { expectedText: e.target.value })}
                  onBlur={() => touch(row.key, 'expectedText')}
                  {...invalid(row, i, 'expectedText')}
                  className={`${FIELD} w-[28rem] max-w-full ${invalidClass(row, i, 'expectedText')}`}
                />
                {errorLine(row, i, 'expectedText')}
              </div>
              {row.checkKind === 'COFA' ? (
                <label className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
                  <input
                    type="checkbox"
                    checked={row.isMandatory}
                    onChange={(e) => update(row.key, { isMandatory: e.target.checked })}
                    className={officeFocus}
                  />
                  Mandatory: a certificate that does not report it cannot pass
                </label>
              ) : null}
            </div>
          ) : null}

          {judgedByText(row) ? null : row.valueKind === 'NUMERIC' ? (
            <div className="flex flex-wrap items-end gap-(--s3)">
              <div className="flex flex-col gap-(--s1)">
                <label htmlFor={`${row.key}-unit`} className={labelClass}>Unit</label>
                <input
                  id={`${row.key}-unit`}
                  value={row.unit}
                  onChange={(e) => update(row.key, { unit: e.target.value })}
                  className={`${FIELD} w-[80px]`}
                />
              </div>
              {num(row, i, 'minValue', 'Reject min')}
              {num(row, i, 'actionLower', 'Action lower')}
              {num(row, i, 'nominal', 'Target')}
              {num(row, i, 'actionUpper', 'Action upper')}
              {num(row, i, 'maxValue', 'Reject max')}
              {num(row, i, 'sampleCount', 'Samples')}
              <div className="flex flex-col gap-(--s1)">
                <label htmlFor={fieldId(row.key, 'aggregation')} className={labelClass}>Judged as</label>
                <select
                  id={fieldId(row.key, 'aggregation')}
                  value={row.aggregation}
                  onChange={(e) => update(row.key, { aggregation: e.target.value as Aggregation })}
                  onBlur={() => touch(row.key, 'aggregation')}
                  {...invalid(row, i, 'aggregation')}
                  className={`${FIELD} ${invalidClass(row, i, 'aggregation')}`}
                >
                  <option value="ALL">{AGGREGATION_LABEL.ALL}</option>
                  <option value="MEAN">{AGGREGATION_LABEL.MEAN}</option>
                  <option value="AQS">{AGGREGATION_LABEL.AQS}</option>
                </select>
                {errorLine(row, i, 'aggregation')}
              </div>
            </div>
          ) : (
            <label className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
              <input
                type="checkbox"
                checked={row.requiresEvidenceOnFail}
                onChange={(e) => update(row.key, { requiresEvidenceOnFail: e.target.checked })}
                className={officeFocus}
              />
              A comment is required when the answer is No
            </label>
          )}

          <div className="flex flex-wrap items-end gap-(--s3)">
            {!isProduct || row.section ? (
              <div className="flex flex-col gap-(--s1)">
                <label htmlFor={`${row.key}-section`} className={labelClass}>Section</label>
                <input
                  id={`${row.key}-section`}
                  value={row.section}
                  onChange={(e) => update(row.key, { section: e.target.value })}
                  className={`${FIELD} min-w-[180px]`}
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-(--s1)">
              <label htmlFor={`${row.key}-cp`} className={labelClass}>Control point</label>
              <select
                id={`${row.key}-cp`}
                value={row.controlPointType}
                onChange={(e) => update(row.key, { controlPointType: e.target.value as ControlPointType })}
                className={FIELD}
              >
                <option value="NONE">None</option>
                <option value="CCP">CCP</option>
                <option value="OPRP">OPRP</option>
              </select>
            </div>
            <label className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
              <input
                type="checkbox"
                checked={row.requiresEvidence}
                onChange={(e) => update(row.key, { requiresEvidence: e.target.checked })}
                className={officeFocus}
              />
              Needs evidence
            </label>
            <label className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
              <input
                type="checkbox"
                checked={row.requiresInstrument}
                onChange={(e) => update(row.key, { requiresInstrument: e.target.checked })}
                className={officeFocus}
              />
              Needs an instrument
            </label>
            {row.requiresInstrument ? (
              <div className="flex flex-col gap-(--s1)">
                <label htmlFor={fieldId(row.key, 'instrumentKind')} className={labelClass}>Instrument kind</label>
                <input
                  id={fieldId(row.key, 'instrumentKind')}
                  value={row.instrumentKind}
                  onChange={(e) => update(row.key, { instrumentKind: e.target.value })}
                  onBlur={() => touch(row.key, 'instrumentKind')}
                  placeholder="e.g. moisture analyser"
                  {...invalid(row, i, 'instrumentKind')}
                  className={`${FIELD} min-w-[180px] ${invalidClass(row, i, 'instrumentKind')}`}
                />
                {errorLine(row, i, 'instrumentKind')}
              </div>
            ) : null}
            <div className="ml-auto flex gap-(--s2)">
              <Button variant="ghost" type="button" onClick={() => move(i, -1)} disabled={i === 0}>
                Move up
              </Button>
              <Button variant="ghost" type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1}>
                Move down
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => setRows((rs) => rs.filter((r) => r.key !== row.key))}
              >
                Remove
              </Button>
            </div>
          </div>
        </fieldset>
      ))}

      <div>
        <Button variant="outline" type="button" onClick={() => setRows((rs) => [...rs, blankRow(isProduct)])}>
          Add a parameter
        </Button>
      </div>

      <div className="flex flex-col gap-(--s1)">
        <label htmlFor="draft-why" className={labelClass}>What changed, and why</label>
        <textarea
          id="draft-why"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          rows={2}
          placeholder="e.g. Moisture limit set from the September trial results."
          className={`${fieldClass} ${officeFocus} min-h-[64px] py-2`}
        />
      </div>

      {problems.warnings.length > 0 ? (
        <ul className="m-0 list-disc rounded-(--radius) border border-(--border-strong) bg-(--subtle) py-(--s3) pl-(--s5) pr-(--s3) text-(length:--fs-sm)">
          {problems.warnings.map((w) => (
            <li key={w.message}>{w.message}</li>
          ))}
        </ul>
      ) : null}
      {refusal ? (
        <p role="alert" className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
          {refusal}
        </p>
      ) : null}

      {/* UX-29 (optional part): kept in reach - a long version's Save sat thousands of pixels below the
          row being changed. */}
      <div className="sticky bottom-0 z-10 -mx-(--s4) -mb-(--s4) flex gap-(--s3) rounded-b-(--radius) border-t border-(--border) bg-(--card) px-(--s4) py-(--s3)">
        <Button variant="default" type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save as draft'}
        </Button>
        <Button variant="outline" type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </section>
  );
}

export default SpecDraftEditor;
